import { Button, Divider, Grid, Typography } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseSwitch from "components/Shared/Base/BaseSwitch";
import BaseTextField from "components/Shared/Base/BaseTextField";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import { useAuth } from "hooks";
import React, { useEffect, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { formHookInputHelper } from "utils/formHookField";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazySearchUsersQuery } from "store/services/userApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import GetCurrentLocation from "utils/getCurrentLocation";

const INITIAL_STATE = {
    account: null,
    entity: null,
    name: "",
    companyName: "",
    contactPersonName: "",
    phone: "",
    alternatePhone: "",
    email: "",
    warehouseAddress: { lat: "", lon: "" },
    corporateAddress: { lat: "", lon: "" },
    isActive: true,
    industryType: "",
    estimatedMonthlyVolume: "",
    currentProvider: "",
    painPoints: "",
    preferredRoutes: "",
    preferredServiceType: "",
    vendorLeadSourceType: null,
    responsible: null,
};

const arrayToString = (val) =>
    Array.isArray(val) ? val.join(", ") : val ?? "";

const stringToArray = (val) =>
    typeof val === "string" && val.trim()
        ? val.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

export const VendorLeadForm = ({ leadDetail, isBusy, onSubmit = () => { } }) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
    const isInitialized = useRef(false);
    const searchTimerRef = useRef(null);

    const { data: accounts, isLoading: loadingAccounts } =
        useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();
    const [searchUsers, { data: userResults, isLoading: searchingUsers }] =
        useLazySearchUsersQuery();
    const { data: vendorLeadSourceTypeList = [], isLoading: loadingSourceTypes } = useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VENDOR_LEAD_SOURCE_TYPE,
        status: "Active",
    });

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || null,
            account: viewEntity?.entity?.account || null,
        },
        mode: "onChange",
    });

    const _watchAccount = useWatch({ control, name: "account" });

    useEffect(() => {
        if (!isInitialized.current) {
            reset(
                leadDetail
                    ? {
                        ...INITIAL_STATE,
                        ...leadDetail,
                        account: leadDetail.account || null,
                        entity: leadDetail.entity || null,
                        warehouseAddress: leadDetail.warehouseAddress || { lat: "", lon: "" },
                        corporateAddress: leadDetail.corporateAddress || { lat: "", lon: "" },
                        painPoints: arrayToString(leadDetail.painPoints),
                        responsible: leadDetail.responsible || null,
                        vendorLeadSourceType: leadDetail.vendorLeadSourceType || null,
                        preferredRoutes: arrayToString(leadDetail.preferredRoutes),
                        estimatedMonthlyVolume: leadDetail.estimatedMonthlyVolume ?? "",
                    }
                    : {
                        ...INITIAL_STATE,
                        entity: viewEntity?.entity || null,
                        account: viewEntity?.entity?.account || null,
                    }
            );
            isInitialized.current = true;
        }
        return () => {
            isInitialized.current = false;
        };
    }, [leadDetail, reset, viewEntity]);

    useEffect(() => {
        if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
            fetchEntities({ account: _watchAccount._id });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    const handleFormSubmit = (data) => {
        onSubmit({
            ...data,
            account: data?.account?._id,
            entity: data?.entity?._id,
            responsible: data?.responsible?._id ?? null,
            vendorLeadSourceType: data?.vendorLeadSourceType?._id ?? null,
            painPoints: stringToArray(data.painPoints),
            preferredRoutes: stringToArray(data.preferredRoutes),
            estimatedMonthlyVolume: data.estimatedMonthlyVolume
                ? Number(data.estimatedMonthlyVolume)
                : undefined,
            warehouseAddress: {
                lat: data.warehouseAddress?.lat ? Number(data.warehouseAddress.lat) : undefined,
                lon: data.warehouseAddress?.lon ? Number(data.warehouseAddress.lon) : undefined,
            },
            corporateAddress: {
                lat: data.corporateAddress?.lat ? Number(data.corporateAddress.lat) : undefined,
                lon: data.corporateAddress?.lon ? Number(data.corporateAddress.lon) : undefined,
            },
        });
    };

    const handleWarehouseLocation = ({ lat, lng }) => {
        setValue("warehouseAddress", { lat, lon: lng }, { shouldDirty: true });
    };

    const handleCorporateLocation = ({ lat, lng }) => {
        setValue("corporateAddress", { lat, lon: lng }, { shouldDirty: true });
    };

    return (
        <AppForm loading={isBusy}>
            <AppForm.Content>
                <Grid container spacing={4}>

                    {/* ── Account & Entity (system admin only) ── */}
                    {isSystemAdmin && (
                        <>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="account"
                                    control={control}
                                    rules={{ required: "Please select an account." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field}
                                            fullWidth
                                            label="Account"
                                            required
                                            value={field.value ?? null}
                                            onChange={(_, data) => {
                                                field.onChange(data);
                                                setValue("entity", null);
                                            }}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option?._id === value?._id
                                            }
                                            options={accounts || []}
                                            loading={loadingAccounts}
                                            helperText={errors?.account?.message}
                                            error={!!errors?.account}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="entity"
                                    control={control}
                                    rules={{ required: "Please select an entity." }}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            required
                                            label="Entity"
                                            value={value ?? null}
                                            onChange={(_, data) => onChange(data)}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option?._id === value?._id
                                            }
                                            options={entities || []}
                                            loading={loadingEntities}
                                            helperText={errors?.entity?.message}
                                            error={!!errors?.entity}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    {/* ── Individual Name ── */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" mb={1}>
                            Individual Name
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="name"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Name"
                                />
                            )}
                        />
                    </Grid>

                    {/* ── Company / Contact ── */}
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>
                            Company / Contact
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="companyName"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Company Name"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="contactPersonName"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Contact Person Name"
                                />
                            )}
                        />
                    </Grid>

                    {/* ── Contact Information ── */}
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>
                            Contact Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <CustomCountryPhoneInput
                                    placeholder="Phone Number"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="alternatePhone"
                            control={control}
                            render={({ field }) => (
                                <CustomCountryPhoneInput
                                    placeholder="Alternate Phone"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: "Enter a valid email address",
                                },
                            }}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Email"
                                    type="email"
                                />
                            )}
                        />
                    </Grid>

                    {/* ── Address ── */}
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>
                            Address
                        </Typography>
                    </Grid>

                    {/* Warehouse Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            Warehouse Address
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Controller
                                    name="warehouseAddress.lat"
                                    control={control}
                                    render={(props) => (
                                        <BaseTextField
                                            fullWidth
                                            {...formHookInputHelper(props)}
                                            label="Latitude"
                                            type="number"
                                            inputProps={{ step: "any" }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="warehouseAddress.lon"
                                    control={control}
                                    render={(props) => (
                                        <BaseTextField
                                            fullWidth
                                            {...formHookInputHelper(props)}
                                            label="Longitude"
                                            type="number"
                                            inputProps={{ step: "any" }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <GetCurrentLocation onLocationRetrieved={handleWarehouseLocation} />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Corporate Address */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            Corporate Address
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Controller
                                    name="corporateAddress.lat"
                                    control={control}
                                    render={(props) => (
                                        <BaseTextField
                                            fullWidth
                                            {...formHookInputHelper(props)}
                                            label="Latitude"
                                            type="number"
                                            inputProps={{ step: "any" }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="corporateAddress.lon"
                                    control={control}
                                    render={(props) => (
                                        <BaseTextField
                                            fullWidth
                                            {...formHookInputHelper(props)}
                                            label="Longitude"
                                            type="number"
                                            inputProps={{ step: "any" }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <GetCurrentLocation onLocationRetrieved={handleCorporateLocation} />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ── Qualification & Commercial ── */}
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>
                            Qualification & Commercial
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="industryType"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Industry Type"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="currentProvider"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Current Provider"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="estimatedMonthlyVolume"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Estimated Monthly Volume"
                                    type="number"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="vendorLeadSourceType"
                            control={control}
                            rules={{ required: "Please select a Lead Source Type." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Lead Source Type"
                                    value={value ?? null}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) => option?.name ?? ""}
                                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                    options={vendorLeadSourceTypeList || []}
                                    loading={loadingSourceTypes}
                                    helperText={errors?.vendorLeadSourceType?.message}
                                    error={!!errors?.vendorLeadSourceType}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="preferredServiceType"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Preferred Service Type"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="painPoints"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Pain Points"
                                    placeholder="e.g. Slow delivery, High cost (comma-separated)"
                                    multiline
                                    rows={2}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="preferredRoutes"
                            control={control}
                            render={(props) => (
                                <BaseTextField
                                    fullWidth
                                    {...formHookInputHelper(props)}
                                    label="Preferred Routes"
                                    placeholder="e.g. KTM-PKR, KTM-BRT (comma-separated)"
                                    multiline
                                    rows={2}
                                />
                            )}
                        />
                    </Grid>

                    {/* ── Ownership ── */}
                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>
                            Responsible Person
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="responsible"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Responsible"
                                    value={value ?? null}
                                    onChange={(_, data) => onChange(data)}
                                    onInputChange={(_, val) => {
                                        clearTimeout(searchTimerRef.current);
                                        searchTimerRef.current = setTimeout(() => {
                                            if (val) searchUsers({ q: val });
                                        }, 300);
                                    }}
                                    getOptionLabel={(option) => option?.name ?? ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={userResults || []}
                                    loading={searchingUsers}
                                    filterOptions={(x) => x}
                                />
                            )}
                        />
                    </Grid>

                    {/* ── isActive (edit only) ── */}
                    {!!leadDetail && (
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={(props) => (
                                    <BaseSwitch
                                        {...formHookInputHelper(props)}
                                        label="Is Active?"
                                    />
                                )}
                            />
                        </Grid>
                    )}

                </Grid>
            </AppForm.Content>

            <AppForm.Actions
                paddingX={3}
                paddingY={2}
                gap={2}
                style={{ display: "flex", justifyContent: "flex-end" }}
            >
                <Button disabled={isBusy} onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isBusy}
                    onClick={handleSubmit(handleFormSubmit)}
                >
                    {leadDetail ? "Update" : "Add Lead"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};